// radio — default skin module
// Token disposition: pass-through (no overrides)
// NO external imports — plain JavaScript only

export const styles = `
  :host {
    display: inline-flex;
    align-items: flex-start;
    gap: var(--run-radio-label-gap, var(--run-space-semantic-sm));
    cursor: pointer;
    color: var(--run-color-text-primary);
  }

  :host([disabled]) {
    opacity: var(--run-opacity-state-disabled);
    cursor: not-allowed;
    pointer-events: none;
  }

  :host(:focus-visible) {
    outline: 2px solid var(--run-color-focus-ring);
    outline-offset: 2px;
  }

  .hit-area {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: calc(var(--run-radio-size, 1rem) + 0.5rem);
    height: calc(var(--run-radio-size, 1rem) + 0.5rem);
    border-radius: var(--run-radius-semantic-control);
  }

  .hit-area::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: var(--run-radius-semantic-control);
    background: transparent;
    transition:
      background var(--run-motion-semantic-micro-duration) var(--run-motion-semantic-micro-easing);
  }

  :host(:not([disabled]):not([readonly])) .hit-area:hover::before {
    background: var(--run-radio-hover-surface, var(--run-color-state-hover-overlay));
  }

  :host(:not([disabled]):not([readonly])) .hit-area:active::before {
    background: var(--run-color-state-active-overlay);
  }

  .indicator {
    position: relative;
    width: var(--run-radio-size, 1rem);
    height: var(--run-radio-size, 1rem);
    border-radius: var(--run-radius-semantic-pill);
    border: 2px solid var(--run-radio-border-color, var(--run-color-border-input-default));
    background: transparent;
    box-sizing: border-box;
    transition:
      border-color var(--run-motion-semantic-micro-duration) var(--run-motion-semantic-micro-easing),
      background var(--run-motion-semantic-micro-duration) var(--run-motion-semantic-micro-easing);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  :host(:not([disabled]):not([readonly])) .hit-area:hover .indicator {
    border-color: var(--run-radio-border-color-hover, var(--run-color-border-input-hover));
  }

  :host([checked]) .indicator {
    border-color: var(--run-radio-fill-checked, var(--run-color-intent-primary-base));
    background: var(--run-radio-fill-checked, var(--run-color-intent-primary-base));
  }

  :host([checked]:not([disabled]):not([readonly])) .hit-area:hover .indicator {
    border-color: var(--run-radio-fill-checked-hover, var(--run-color-intent-primary-hover));
    background: var(--run-radio-fill-checked-hover, var(--run-color-intent-primary-hover));
  }

  :host([checked]:not([disabled]):not([readonly])) .hit-area:active .indicator {
    border-color: var(--run-radio-fill-checked-active, var(--run-color-intent-primary-active));
    background: var(--run-radio-fill-checked-active, var(--run-color-intent-primary-active));
  }

  :host([invalid]) .indicator {
    border-color: var(--run-color-intent-danger-base);
  }

  :host([invalid][checked]) .indicator {
    background: var(--run-color-intent-danger-base);
    border-color: var(--run-color-intent-danger-base);
  }

  .dot {
    width: var(--run-radio-dot-size, calc(var(--run-radio-size, 1rem) * 0.5));
    height: var(--run-radio-dot-size, calc(var(--run-radio-size, 1rem) * 0.5));
    border-radius: var(--run-radius-semantic-pill);
    background: var(--run-radio-dot-color, var(--run-color-intent-primary-on));
    transform: scale(0);
    transition:
      transform var(--run-motion-semantic-micro-duration) var(--run-motion-semantic-micro-easing);
  }

  :host([checked]) .dot {
    transform: scale(1);
  }

  .label-wrapper {
    flex: 1;
    min-inline-size: 0;
    padding-block-start: calc((var(--run-radio-size, 1rem) + 0.5rem - 1lh) / 2);
    color: var(--run-color-text-primary);
  }

  .description-wrapper {
    display: block;
    padding-block-start: var(--run-space-semantic-2xs);
    color: var(--run-color-text-secondary);
  }

  .description-wrapper[hidden] {
    display: none;
  }
`;

export function render(host) {
  const { checked, disabled, invalid, readonly, _descriptionSlotFilled } = host; // declared in MVA SkinModule.render contract

  const labelId = host._labelId || '';
  const descId = host._descId || '';

  host.shadowRoot.innerHTML = `
    <span class="hit-area" aria-hidden="true">
      <span class="indicator" part="indicator" aria-hidden="true">
        <span class="dot" part="dot"></span>
      </span>
    </span>
    <span class="label-wrapper" id="${labelId}" part="label-wrapper">
      <slot></slot>
    </span>
    <span class="description-wrapper" id="${descId}" part="description-wrapper"${_descriptionSlotFilled ? '' : ' hidden'}>
      <slot name="description"></slot>
    </span>
  `;
}